import * as React from 'react';
import { sentryCaptureException } from '../utils/sentry/sentry-client';
import DefaultFeatureFlagsContext from '../contexts/default-feature-flags-context';

/** @visibleForTesting */
export type DefaultFlags = React.ContextType<typeof DefaultFeatureFlagsContext>;

/**
 * Hook to access feature flags from the DefaultFeatureFlagsContext
 *
 * Uses server-side detection to handle missing context appropriately:
 * - Server-side: Throws error to prevent publishing broken pages
 * - Client-side: Logs to Sentry and returns empty object for graceful degradation
 */
export const useDefaultFeatureFlags = (): DefaultFlags => {
    const context = React.useContext(DefaultFeatureFlagsContext);

    if (!context) {
        const error = new Error(
            'useDefaultFeatureFlags must be used within DefaultFeatureFlagsContext.Provider'
        );

        // Server-side: Throw error to prevent publishing broken pages
        if (typeof window === 'undefined') {
            throw error;
        }

        // Client-side: Log to Sentry and return empty object for graceful degradation
        sentryCaptureException({ exception: error });
        return {};
    }

    return context;
};
